import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import Config from 'react-native-config';
import { Platform } from 'react-native';
import { requestNotificationsOnUserGesture } from './requestNotificationsOnUserGesture';

export type ChatwootColorScheme = 'light' | 'dark' | 'auto';

export type ChatwootConfig = {
  websiteToken: string;
  baseUrl: string;
  locale: string;
  colorScheme: ChatwootColorScheme;
};

export type ChatwootUser = {
  identifier: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  avatarUrl?: string;
  identifierHash?: string;
};

export type ChatwootWidgetUser = {
  identifier: string;
  name?: string;
  email?: string;
  phone_number?: string;
  avatar_url?: string;
  identifier_hash?: string;
};

export type ChatwootCustomAttributes = Record<
  string,
  string | number | boolean | null | undefined
>;

export type ChatwootErrorCode = 'missing-config' | 'offline' | 'unknown';

export type ChatwootError = {
  id: string;
  code: ChatwootErrorCode;
  message: string;
  cause?: unknown;
};

export type ChatwootUnreadApiPayload = {
  unread_count: number;
  has_unread: boolean;
  last_message_preview?: string | null;
  last_message_at?: string | null;
};

export type ChatwootUnreadState = {
  unreadCount: number;
  hasUnread: boolean;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
};

export type ChatwootState = {
  initialized: boolean;
  isVisible: boolean;
  config: ChatwootConfig;
  user: ChatwootWidgetUser | null;
  customAttributes: ChatwootCustomAttributes;
  unread: ChatwootUnreadState;
  lastError: ChatwootError | null;
};

export type ChatwootOpenResult =
  | { ok: true }
  | { ok: false; error: ChatwootError };

type ChatwootListener = (state: ChatwootState) => void;
type ChatwootOpenListener = () => void | Promise<void>;

const CHATWOOT_COOKIE_STORAGE_KEY = 'cwCookie';
const CHATWOOT_PENDING_PUSH_OPEN_KEY = 'chatwootPendingPushOpen';
const CHATWOOT_AGENT_MESSAGE_TYPE = 'CHATWOOT_AGENT_MESSAGE';

const emptyUnreadState: ChatwootUnreadState = {
  unreadCount: 0,
  hasUnread: false,
  lastMessagePreview: null,
  lastMessageAt: null,
};

const defaultConfig: ChatwootConfig = {
  websiteToken: '',
  baseUrl: '',
  locale: 'en',
  colorScheme: 'auto',
};

const trimEnv = (value?: string) =>
  typeof value === 'string' ? value.trim().replace(/^["']|["']$/g, '') : '';

const CHATWOOT_WEBSITE_TOKEN = trimEnv(Config.CHATWOOT_WEBSITE_TOKEN || process.env.CHATWOOT_WEBSITE_TOKEN);
const CHATWOOT_BASE_URL = trimEnv(Config.CHATWOOT_BASE_URL || process.env.CHATWOOT_BASE_URL);
const CHATWOOT_LOCALE = trimEnv(Config.CHATWOOT_LOCALE || process.env.CHATWOOT_LOCALE);
const CHATWOOT_COLOR_SCHEME = trimEnv(Config.CHATWOOT_COLOR_SCHEME || process.env.CHATWOOT_COLOR_SCHEME);

const cleanBaseUrl = (url: string) => url.replace(/\/+$/, '');

const normalizeUser = (user: ChatwootUser): ChatwootWidgetUser => ({
  identifier: user.identifier,
  name: user.name,
  email: user.email,
  phone_number: user.phoneNumber,
  avatar_url: user.avatarUrl,
  identifier_hash: user.identifierHash,
});

class ChatwootServiceController {
  private state: ChatwootState = {
    initialized: false,
    isVisible: false,
    config: defaultConfig,
    user: null,
    customAttributes: {},
    unread: emptyUnreadState,
    lastError: null,
  };

  private listeners = new Set<ChatwootListener>();
  private openListeners = new Set<ChatwootOpenListener>();

  initialize(overrides: Partial<ChatwootConfig> = {}) {
    const websiteToken = overrides.websiteToken ?? CHATWOOT_WEBSITE_TOKEN;
    const baseUrl = cleanBaseUrl(overrides.baseUrl ?? CHATWOOT_BASE_URL);
    this.state = {
      ...this.state,
      initialized: true,
      config: {
        websiteToken,
        baseUrl,
        locale: overrides.locale ?? (CHATWOOT_LOCALE || 'en'),
        colorScheme:
          overrides.colorScheme ??
          ((CHATWOOT_COLOR_SCHEME as ChatwootColorScheme) || 'auto'),
      },
    };
    this.emit();
  }

  identifyUser(
    user: ChatwootUser,
    customAttributes: ChatwootCustomAttributes = {},
  ) {
    if (!user.identifier) {
      this.identifyAnonymous(customAttributes);
      return;
    }

    this.state = {
      ...this.state,
      user: normalizeUser(user),
      customAttributes,
      lastError: null,
    };
    this.emit();
  }

  identifyAnonymous(customAttributes: ChatwootCustomAttributes = {}) {
    this.state = {
      ...this.state,
      user: null,
      customAttributes,
      lastError: null,
    };
    this.emit();
  }

  async openChat(options?: {
    /** Default true. Set false for push-resume opens (already authorized). */
    requestNotificationPermission?: boolean;
  }): Promise<ChatwootOpenResult> {
    if (!this.state.initialized) {
      this.initialize();
    }

    if (!this.isConfigured()) {
      return this.fail(
        'missing-config',
        'Customer support chat is not configured.',
      );
    }

    try {
      // High-intent icon tap: ask for notification permission so logged-out
      // login nudges / support push can work. Skip for push-driven opens.
      if (options?.requestNotificationPermission !== false) {
        try {
          await requestNotificationsOnUserGesture();
        } catch {
          // Permission failure must not block support chat.
        }
      }

      const networkState = await NetInfo.fetch();
      if (networkState.isConnected === false) {
        return this.fail(
          'offline',
          'You are offline. Connect to the internet to open support chat.',
        );
      }

      this.setWidgetVisible(true);
      this.emitOpened();
      return { ok: true };
    } catch (error) {
      return this.fail('unknown', 'Unable to open support chat.', error);
    }
  }

  closeChat() {
    this.setWidgetVisible(false);
  }

  isChatwootAgentMessageData(data: unknown) {
    return (
      data !== null &&
      typeof data === 'object' &&
      (data as Record<string, unknown>).type === CHATWOOT_AGENT_MESSAGE_TYPE
    );
  }

  async queueOpenFromPush(data: unknown) {
    if (!this.isChatwootAgentMessageData(data)) return false;

    await AsyncStorage.setItem(
      CHATWOOT_PENDING_PUSH_OPEN_KEY,
      JSON.stringify({
        createdAt: Date.now(),
      }),
    );
    return true;
  }

  async consumePendingPushOpen() {
    const pending = await AsyncStorage.getItem(CHATWOOT_PENDING_PUSH_OPEN_KEY);
    if (!pending) return false;

    await AsyncStorage.removeItem(CHATWOOT_PENDING_PUSH_OPEN_KEY);
    return true;
  }

  async openChatFromPush(data: unknown) {
    if (!this.isChatwootAgentMessageData(data)) return false;

    const unreadCount = Number(
      (data as Record<string, unknown>).unread_count || 0,
    );
    this.setUnreadCount(unreadCount);
    const result = await this.openChat({
      requestNotificationPermission: false,
    });
    if (result.ok) {
      await AsyncStorage.removeItem(CHATWOOT_PENDING_PUSH_OPEN_KEY);
    }
    return result.ok;
  }

  setWidgetVisible(isVisible: boolean) {
    this.state = {
      ...this.state,
      isVisible,
    };
    this.emit();
  }

  async reset() {
    // The official Chatwoot RN widget stores its conversation auth token here.
    await AsyncStorage.removeItem(CHATWOOT_COOKIE_STORAGE_KEY);
    this.state = {
      ...this.state,
      isVisible: false,
      user: null,
      customAttributes: {},
      unread: emptyUnreadState,
      lastError: null,
    };
    this.emit();
  }

  isConfigured() {
    const { websiteToken, baseUrl } = this.state.config;
    return Boolean(websiteToken && baseUrl);
  }

  setUnreadFromApi(payload: ChatwootUnreadApiPayload) {
    const unreadCount = Number(payload.unread_count || 0);
    this.state = {
      ...this.state,
      unread: {
        unreadCount,
        hasUnread: Boolean(payload.has_unread && unreadCount > 0),
        lastMessagePreview: payload.last_message_preview ?? null,
        lastMessageAt: payload.last_message_at ?? null,
      },
    };
    this.emit();
  }

  setUnreadCount(unreadCount: number) {
    const normalizedCount = Math.max(0, Number(unreadCount || 0));
    this.state = {
      ...this.state,
      unread: {
        ...this.state.unread,
        unreadCount: normalizedCount,
        hasUnread: normalizedCount > 0,
      },
    };
    this.emit();
  }

  clearUnread() {
    this.state = {
      ...this.state,
      unread: emptyUnreadState,
    };
    this.emit();
  }

  getSnapshot(): ChatwootState {
    return this.state;
  }

  subscribe(listener: ChatwootListener) {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribeToOpen(listener: ChatwootOpenListener) {
    this.openListeners.add(listener);
    return () => {
      this.openListeners.delete(listener);
    };
  }

  private fail(
    code: ChatwootErrorCode,
    message: string,
    cause?: unknown,
  ): ChatwootOpenResult {
    const error: ChatwootError = {
      id: `${code}-${Date.now()}`,
      code,
      message,
      cause,
    };
    this.state = {
      ...this.state,
      lastError: error,
      isVisible: false,
    };
    this.emit();
    return { ok: false, error };
  }

  private emit() {
    const snapshot = this.getSnapshot();
    this.listeners.forEach(listener => listener(snapshot));
  }

  private emitOpened() {
    this.openListeners.forEach(listener => {
      void listener();
    });
  }
}

export const ChatwootService = new ChatwootServiceController();

export const openChatwootSupport = () => ChatwootService.openChat();

export const resetChatwootSupport = () => ChatwootService.reset();

export const chatwootRuntimePlatform = Platform.OS;

export default ChatwootService;
